import { zodResolver } from "@hookform/resolvers/zod";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Button, Dialog, Divider, IconButton, InputAdornment, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import api from "../../api";
import { useLogin } from "../../context/Login/LoginContext";
import { getBackendUrl } from "../../util/envFn";
import ErrorBox from "../ErrorBox";
import GoogleLoginButton from "../GoogleLoginButton";
import { hashPassword } from "../../util/hash";

type LoginDialogProps = {
    open: boolean;
    onClose: () => void;
}

// Define the validation schema with Zod
const loginSchema = z.object({
    email: z.string()
        .min(1, "Email is required")
        .email("Invalid email address"),
    password: z.string()
        .min(1, "Password is required")
        .min(6, "Password must be at least 6 characters")
});

// Registration schema with additional fields
const registerSchema = z.object({
    name: z.string().min(1, "Name is required"),
    email: z.string()
        .min(1, "Email is required")
        .email("Invalid email address"),
    password: z.string()
        .min(1, "Password is required")
        .min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password")
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});

// Infer the types from the schemas
type LoginFormInput = z.infer<typeof loginSchema>;
type RegisterFormInput = z.infer<typeof registerSchema>;

type PopupData = {
    title: string;
    label: string;
    onClick: () => void;
}

const LoginDialog = ({ open, onClose }: LoginDialogProps) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [tabValue, setTabValue] = useState(0); // 0 for login, 1 for register
    const { login } = useLogin();
    const { enqueueSnackbar } = useSnackbar();
    const [error, setError] = useState<string | null>(null);

    const { register: registerLogin, handleSubmit: handleLoginSubmit, reset: resetLogin, formState: { errors: loginErrors } } = useForm<LoginFormInput>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: "",
            password: ""
        }
    });

    const { register: registerRegister, handleSubmit: handleRegisterSubmit, reset: resetRegister, formState: { errors: registerErrors } } = useForm<RegisterFormInput>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            name: "",
            email: "",
            password: "",
            confirmPassword: ""
        }
    });

    const onLoginSubmit: SubmitHandler<LoginFormInput> = async (data) => {
        try {
            const hashedPassword = await hashPassword(data.password);
            api.post(getBackendUrl('/login'), {
                email: data.email,
                password: hashedPassword
            })
                .then(res => {
                    login(res.data.user, res.data.token);
                    enqueueSnackbar('Login successful', { variant: 'success' });
                    handleClose();
                })
                .catch(err => {
                    setError(err.response?.data?.message || 'Login failed');
                });
        } catch (error) {
            setError('An error occurred during login. Please try again.');
        }
    };

    const onRegisterSubmit: SubmitHandler<RegisterFormInput> = async (data) => {
        try {
            const hashedPassword = await hashPassword(data.password);
            api.post(getBackendUrl('/register'), {
                fullName: data.name,
                email: data.email,
                password: hashedPassword
            })
                .then(res => {
                    enqueueSnackbar('Registration successful, please login', { variant: 'success' });
                    setTabValue(0);
                })
                .catch(err => {
                    setError(err.response?.data?.message || 'Registration failed');
                });
        } catch (error) {
            setError('An error occurred during registration. Please try again.');
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        changeTab(newValue);
    };

    const changeTab = (newValue: number) => {
        resetLogin();
        resetRegister();
        setError(null);
        setTabValue(newValue);
    };

    const handleClose = () => {
        resetLogin();
        resetRegister();
        setError(null);
        setTabValue(0);
        onClose();
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 4,
                        pb: 6,
                        width: { xs: '90%', sm: 300 },
                        // maxWidth: 360,
                    }
                }}
            >
                <Stack gap={2} alignItems="center">
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        textAlign="center"
                    >
                        {tabValue === 0 ? 'Welcome Back' : 'Create Account'}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                    >
                        {tabValue === 0 ? 'Sign in to access the job portal' : 'Sign up to join our platform'}
                    </Typography>

                    <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth" sx={{ width: '100%' }}>
                        <Tab label="Login" />
                        <Tab label="Register" />
                    </Tabs>

                    <Divider sx={{ width: '100%' }} />
                    <Stack gap={1.5} width="100%">
                        {tabValue === 0 ? (
                            // Login Form
                            <form onSubmit={handleLoginSubmit(onLoginSubmit)}>
                                <Stack gap={2}>
                                    <TextField
                                        label="Email"
                                        variant="outlined"
                                        fullWidth
                                        type="email"
                                        {...registerLogin("email")}
                                        error={!!loginErrors.email}
                                        helperText={loginErrors.email?.message}
                                        size="small"
                                    />

                                    <TextField
                                        label="Password"
                                        variant="outlined"
                                        fullWidth
                                        type={showPassword ? "text" : "password"}
                                        {...registerLogin("password")}
                                        error={!!loginErrors.password}
                                        helperText={loginErrors.password?.message}
                                        size="small"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={togglePasswordVisibility}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <ErrorBox error={error} />

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                            mt: 1,
                                            borderRadius: 2,
                                            py: 1
                                        }}
                                    >
                                        Sign In
                                    </Button>
                                </Stack>
                            </form>
                        ) : (
                            // Registration Form
                            <form onSubmit={handleRegisterSubmit(onRegisterSubmit)}>
                                <Stack gap={2}>
                                    <TextField
                                        label="Full Name"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                        {...registerRegister("name")}
                                        error={!!registerErrors.name}
                                        helperText={registerErrors.name?.message}
                                        size="small"
                                    />

                                    <TextField
                                        label="Email"
                                        variant="outlined"
                                        fullWidth
                                        type="email"
                                        {...registerRegister("email")}
                                        error={!!registerErrors.email}
                                        helperText={registerErrors.email?.message}
                                        size="small"
                                    />

                                    <TextField
                                        label="Password"
                                        variant="outlined"
                                        fullWidth
                                        type={showPassword ? "text" : "password"}
                                        {...registerRegister("password")}
                                        error={!!registerErrors.password}
                                        helperText={registerErrors.password?.message}
                                        size="small"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={togglePasswordVisibility}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />

                                    <TextField
                                        label="Confirm Password"
                                        variant="outlined"
                                        fullWidth
                                        type={showConfirmPassword ? "text" : "password"}
                                        {...registerRegister("confirmPassword")}
                                        error={!!registerErrors.confirmPassword}
                                        helperText={registerErrors.confirmPassword?.message}
                                        size="small"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle confirm password visibility"
                                                        onClick={toggleConfirmPasswordVisibility}
                                                        edge="end"
                                                    >
                                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <ErrorBox error={error} />

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                            mt: 1,
                                            borderRadius: 2,
                                            py: 1
                                        }}
                                    >
                                        Create Account
                                    </Button>
                                </Stack>
                            </form>
                        )}

                        <Divider sx={{ my: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                or
                            </Typography>
                        </Divider>
                        <GoogleLoginButton />
                    </Stack>
                </Stack>
            </Dialog>
        </>
    )
}

export default LoginDialog;
